/**
 * Objectifs sportifs — §B. Objectifs typés (distance, poids, force, fréquence…)
 * avec direction (augmenter / diminuer / maintenir). Le % d'avancement est
 * calculé côté backend (robuste aux objectifs décroissants). On peut ajouter
 * un point de mesure : la valeur courante se met à jour et l'objectif se
 * clôture automatiquement à 100 %.
 */
import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'

import { performanceApi } from '../api/performanceApi'
import {
  GOAL_TYPE_LABELS,
  Loading,
  PERF_ACCENT,
  Pill,
  ProgressBar,
  cardSx,
  darkFieldSx,
} from './shared'

const GOAL_TYPES = Object.entries(GOAL_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}))

const DIRECTIONS = [
  { value: 'increase', label: 'Augmenter' },
  { value: 'decrease', label: 'Diminuer' },
  { value: 'maintain', label: 'Maintenir' },
]

const STATUS_LABELS = {
  active: 'En cours',
  achieved: 'Atteint',
  paused: 'En pause',
  missed: 'Manqué',
  archived: 'Archivé',
}

const STATUS_COLOR = {
  active: PERF_ACCENT,
  achieved: '#3ddc97',
  paused: '#5b8cff',
  missed: '#ff4d4f',
  archived: 'rgba(255,255,255,0.4)',
}

const EMPTY = {
  title: '',
  goal_type: 'distance',
  direction: 'increase',
  baseline_value: '',
  current_value: '',
  target_value: '',
  unit: 'km',
  deadline: '',
  priority: 2,
}

export default function GoalsView() {
  const [goals, setGoals] = useState([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState(EMPTY)
  const [measure, setMeasure] = useState(null) // goal
  const [measureValue, setMeasureValue] = useState('')
  const [busy, setBusy] = useState(false)

  const load = async () => {
    setLoading(true)
    const list = await performanceApi.goals.list()
    setGoals(list)
    setLoading(false)
  }

  useEffect(() => {
    load()
  }, [])

  const setField = (f, v) => setForm((p) => ({ ...p, [f]: v }))

  const save = async () => {
    if (!form.title) return
    setBusy(true)
    try {
      await performanceApi.goals.create({
        title: form.title,
        goal_type: form.goal_type,
        direction: form.direction,
        baseline_value: form.baseline_value || null,
        current_value: form.current_value || null,
        target_value: form.target_value || null,
        unit: form.unit,
        deadline: form.deadline || null,
        priority: Number(form.priority),
      })
      setCreating(false)
      setForm(EMPTY)
      await load()
    } finally {
      setBusy(false)
    }
  }

  const saveMeasure = async () => {
    if (!measure || measureValue === '') return
    setBusy(true)
    try {
      await performanceApi.addGoalMeasurement(measure.id, { value: measureValue })
      setMeasure(null)
      setMeasureValue('')
      await load()
    } finally {
      setBusy(false)
    }
  }

  if (loading) return <Loading label="Chargement de vos objectifs…" />

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 18 }}>
          Mes objectifs sportifs
        </Typography>
        <Button
          startIcon={<AddRoundedIcon />}
          onClick={() => setCreating(true)}
          sx={{
            bgcolor: PERF_ACCENT,
            color: '#0b0f1f',
            fontWeight: 700,
            textTransform: 'none',
            '&:hover': { bgcolor: '#ff7d56' },
          }}
        >
          Nouvel objectif
        </Button>
      </Stack>

      {goals.length === 0 ? (
        <Box sx={cardSx}>
          <Typography sx={{ color: 'rgba(255,255,255,0.55)' }}>
            Aucun objectif pour l'instant. Définissez un objectif typé (courir
            10 km, développé couché 100 kg, 4 séances/semaine…) pour que le coach
            suive votre progression.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {goals.map((g) => (
            <Grid item xs={12} md={6} key={g.id}>
              <Box sx={{ ...cardSx, height: '100%' }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  sx={{ mb: 1 }}
                >
                  <Typography sx={{ color: '#fff', fontWeight: 700 }}>
                    {g.title}
                  </Typography>
                  <Pill
                    label={STATUS_LABELS[g.status] || g.status}
                    color={STATUS_COLOR[g.status] || PERF_ACCENT}
                  />
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                  <Pill label={GOAL_TYPE_LABELS[g.goal_type] || g.goal_type} />
                  {g.sport_name && (
                    <Pill label={g.sport_name} color="#5b8cff" />
                  )}
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ mb: 0.5 }}
                >
                  <Typography
                    sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}
                  >
                    {g.current_value ?? '—'} / {g.target_value ?? '—'} {g.unit}
                  </Typography>
                  {g.deadline && (
                    <Typography
                      sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}
                    >
                      échéance {g.deadline}
                    </Typography>
                  )}
                </Stack>
                <ProgressBar pct={g.progress_pct ?? 0} />
                <Button
                  size="small"
                  onClick={() => {
                    setMeasure(g)
                    setMeasureValue('')
                  }}
                  sx={{
                    color: PERF_ACCENT,
                    textTransform: 'none',
                    px: 0,
                    mt: 1,
                  }}
                >
                  + Ajouter une mesure
                </Button>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Création */}
      <Dialog open={creating} onClose={() => setCreating(false)} fullWidth>
        <DialogTitle>Nouvel objectif</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Intitulé"
              placeholder="Ex. Courir 10 km"
              value={form.title}
              onChange={(e) => setField('title', e.target.value)}
              fullWidth
            />
            <Stack direction="row" spacing={2}>
              <TextField
                select
                label="Type"
                value={form.goal_type}
                onChange={(e) => setField('goal_type', e.target.value)}
                fullWidth
              >
                {GOAL_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Direction"
                value={form.direction}
                onChange={(e) => setField('direction', e.target.value)}
                fullWidth
              >
                {DIRECTIONS.map((d) => (
                  <MenuItem key={d.value} value={d.value}>
                    {d.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                type="number"
                label="Départ"
                value={form.baseline_value}
                onChange={(e) => setField('baseline_value', e.target.value)}
                fullWidth
              />
              <TextField
                type="number"
                label="Actuel"
                value={form.current_value}
                onChange={(e) => setField('current_value', e.target.value)}
                fullWidth
              />
              <TextField
                type="number"
                label="Cible"
                value={form.target_value}
                onChange={(e) => setField('target_value', e.target.value)}
                fullWidth
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Unité"
                value={form.unit}
                onChange={(e) => setField('unit', e.target.value)}
                sx={{ width: 120 }}
              />
              <TextField
                type="date"
                label="Échéance"
                InputLabelProps={{ shrink: true }}
                value={form.deadline}
                onChange={(e) => setField('deadline', e.target.value)}
                fullWidth
              />
              <TextField
                select
                label="Priorité"
                value={form.priority}
                onChange={(e) => setField('priority', e.target.value)}
                sx={{ width: 140 }}
              >
                <MenuItem value={1}>Haute</MenuItem>
                <MenuItem value={2}>Moyenne</MenuItem>
                <MenuItem value={3}>Basse</MenuItem>
              </TextField>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreating(false)}>Annuler</Button>
          <Button
            onClick={save}
            disabled={busy}
            variant="contained"
            sx={{ bgcolor: PERF_ACCENT, color: '#0b0f1f' }}
          >
            Créer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mesure */}
      <Dialog open={!!measure} onClose={() => setMeasure(null)}>
        <DialogTitle>Mesure — {measure?.title}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            type="number"
            label={`Valeur (${measure?.unit || ''})`}
            value={measureValue}
            onChange={(e) => setMeasureValue(e.target.value)}
            fullWidth
            sx={{ mt: 1, minWidth: 280 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMeasure(null)}>Annuler</Button>
          <Button
            onClick={saveMeasure}
            disabled={busy}
            variant="contained"
            sx={{ bgcolor: PERF_ACCENT, color: '#0b0f1f' }}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
